/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-23 14:32:27
 **/

import { BaseServiceV2 } from "@/frameworks/libraries/restservice";

class MyRoom extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (myRoomId: string, path?: string) => `/api/my_room/${myRoomId}${path ? `/${path}` : ""}`;

	public inquiry = {
		name: "inquiry",
		execute: ({ myRoomId }: WebServices.MyRoom.Inquiry.Request): Promise<WebServices.MyRoom.Inquiry.Response> =>
			this.get(this.inquiry.name, this.APIPath(myRoomId))
	};

	public participants = {
		name: "participants",
		execute: (
			parameter: WebServices.MyRoom.Participants.Request = {}
		): Promise<WebServices.MyRoom.Participants.Response> =>
			this.get(this.participants.name, this.APIPath(parameter.myRoomId, "participants"), parameter)
	};

	public details = {
		name: "details",
		execute: ({ myRoomId }: WebServices.MyRoom.Details.Request): Promise<WebServices.MyRoom.Details.Response> =>
			this.get(this.details.name, this.APIPath(myRoomId, "details"))
	};

	public participate = {
		name: "participate",
		execute: ({
			myRoomId,
			...parameter
		}: WebServices.MyRoom.Participate.Request): Promise<WebServices.MyRoom.Participate.Response> =>
			this.post(this.participate.name, this.APIPath(myRoomId, "participate"), parameter)
	};

	public leave = {
		name: "leave",
		execute: ({
			myRoomId,
			...parameter
		}: WebServices.MyRoom.Leave.Request): Promise<WebServices.MyRoom.Leave.Response> =>
			this.post(this.leave.name, this.APIPath(myRoomId, "leave"), parameter)
	};

	public update = {
		name: "update",
		execute: ({
			myRoomId,
			...parameter
		}: WebServices.MyRoom.Update.Request): Promise<WebServices.MyRoom.Update.Response> =>
			this.put(this.update.name, this.APIPath(myRoomId), parameter)
	};

	public visitorCount = {
		name: "visitorCount",
		execute: ({
			myRoomId
		}: WebServices.MyRoom.VisitorCount.Request): Promise<WebServices.MyRoom.VisitorCount.Response> =>
			this.get(this.visitorCount.name, this.APIPath(myRoomId, "visitor_count"))
	};

	public authorities = {
		name: "myroom:authorities",
		execute: ({ myRoomId }: WebServices.MyRoom.Authority.Request): Promise<WebServices.MyRoom.Authority.Response> =>
			this.get(this.authorities.name, this.APIPath(myRoomId, "authorities"))
	};
}

class MyRoomGuestBook extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (myRoomId: string, guestBookId?: string, path?: string) =>
		`/api/my_room/${myRoomId}/guest_books${guestBookId ? `/${guestBookId}` : ""}${path ? `/${path}` : ""}`;

	public deleteItem = {
		name: "guestBook:delete",
		execute: ({
			myRoomId,
			guestBookId
		}: WebServices.MyRoomGuestBook.Delete.Request): Promise<WebServices.MyRoomGuestBook.Delete.Response> =>
			this.delete(this.deleteItem.name, this.APIPath(myRoomId, guestBookId))
	};

	public search = {
		name: "guestBook:search",
		execute: (
			parameter: WebServices.MyRoomGuestBook.Search.Request
		): Promise<WebServices.MyRoomGuestBook.Search.Response> =>
			this.get(this.search.name, this.APIPath(parameter.myRoomId), parameter)
	};

	public write = {
		name: "guestBook:write",
		execute: ({
			myRoomId,
			...parameter
		}: WebServices.MyRoomGuestBook.Write.Request): Promise<WebServices.MyRoomGuestBook.Write.Response> =>
			this.post(this.write.name, this.APIPath(myRoomId), parameter)
	};

	public update = {
		name: "guestBook:update",
		execute: ({
			myRoomId,
			guestBookId,
			...parameter
		}: WebServices.MyRoomGuestBook.Update.Request): Promise<WebServices.MyRoomGuestBook.Update.Response> =>
			this.put(this.update.name, this.APIPath(myRoomId, guestBookId), parameter)
	};

	public hide = {
		name: "guestBook:hide",
		execute: ({
			myRoomId,
			guestBookId,
			...parameter
		}: WebServices.MyRoomGuestBook.Hide.Request): Promise<WebServices.MyRoomGuestBook.Hide.Response> =>
			this.put(this.hide.name, this.APIPath(myRoomId, guestBookId, "hide"), parameter)
	};
}

class MyRoomActivity extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (myRoomId: string) => `/api/my_room/${myRoomId}/activities`;

	public activities = {
		name: "myRoom:activites",
		execute: (
			parameter: WebServices.MyRoomActivity.Activities.Request = {}
		): Promise<WebServices.MyRoomActivity.Activities.Response> =>
			this.get(this.activities.name, this.APIPath(parameter.myRoomId), parameter)
	};
}

class MyRoomNotification extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (myRoomId: string) => `/api/my_room/${myRoomId}/space_chat_notification`;

	public updateSpaceChatNotification = {
		name: "myRoom:updateSpaceChatNotification",
		execute: ({
			myRoomId,
			...parameter
		}: WebServices.MyRoom.Notification.Request): Promise<WebServices.MyRoom.Notification.Response> =>
			this.post(this.updateSpaceChatNotification.name, this.APIPath(myRoomId), parameter)
	};
}

class MyRoomGuestInvite extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (spaceId: string, path?: string) => `/api/my_room/${spaceId}${path ? `/${path}` : ""}`;

	public guest = {
		renew: {
			name: "guest:renew",
			execute: ({
				spaceId,
				...parameter
			}: WebServices.MyRoomGuestInvite.Renew.Request): Promise<WebServices.MyRoomGuestInvite.Renew.Response> =>
				this.post(this.guest.renew.name, this.APIPath(spaceId, "guest_invite/renew"), parameter)
		},
		inquiry: {
			name: "guest:inquiry",
			execute: ({
				spaceId,
				...parameter
			}: WebServices.MyRoomGuestInvite.Inquiry.Request): Promise<WebServices.MyRoomGuestInvite.Inquiry.Response> =>
				this.get(this.guest.inquiry.name, this.APIPath(spaceId, "guest_invite/code"), parameter)
		},
		inquiryByKey: {
			name: "guest:inquirybykey",
			execute: (
				parameter: WebServices.MyRoomGuestInvite.InquiryByKey.Request
			): Promise<WebServices.MyRoomGuestInvite.InquiryByKey.Response> =>
				this.get(this.guest.inquiryByKey.name, this.APIPath("guest_invite_code"), parameter)
		}
	};
}

export default class MyRoomService {
	constructor(
		events: API.Events,
		public readonly myRoom = new MyRoom(),
		public readonly myRoomGuestBook = new MyRoomGuestBook(),
		public readonly myRoomActivity = new MyRoomActivity(),
		public readonly myRoomNotification = new MyRoomNotification(),
		public readonly myRoomInvite = new MyRoomGuestInvite()
	) {
		myRoom.set_event(events);
		myRoomGuestBook.set_event(events);
		myRoomActivity.set_event(events);
		myRoomNotification.set_event(events);
		myRoomInvite.set_event(events);
	}
}
